import React, {Fragment, useCallback, useEffect, useState} from 'react';
import {
  useForm,
  useFieldArray,
  Controller,
  get,
  useWatch,
} from 'react-hook-form';
import {Button, FormItem, Select, Input, Tooltip} from '@mayo/mayo-ui-gakki';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogFooter,
} from '@gakki/components/Dialog';
import {ButtonGroup} from '@common/components/Button';
import SubHeader from '@gakki/components/SubHeader';
import {NATION_CODE_OPTIONS, TW_COUNTY_OPTIONS} from '../../const/options';
import {sortByEmployeeJobNo} from 'utils';
import {ReactComponent as CirclePlusIcon} from '@gakki/assets/circle_plus2.svg';
import {ReactComponent as TrashIcon} from '@gakki/assets/trash.svg';
import {ReactComponent as InfoIcon} from '@common/assets/icon_info-circle.svg';
import {DevTool} from '@hookform/devtools';

const MAIN = 0;
const SECONDARY = 1;

const supervisorStatusOptions = [
  {
    value: MAIN,
    label: '主要（正主管）',
  },
  {
    value: SECONDARY,
    label: '次要（副主管）',
  },
];

const Row = ({children, ...props}) => {
  return (
    <div className={'grid grid-cols-2 gap-5'} {...props}>
      {children}
    </div>
  );
};

const SupervisorField = props => {
  const {
    supervisor,
    idx,
    formMethod,
    supervisorsFieldName,
    sortedEmployee,
    mainSupervisors,
    unselectedEmployees,
    setMainSupervisors,
    setUnselectedEmployees,
    isEditRootNode,
    defaultValues,
    onRemove,
    parentNodeSupervisors,
    isSubmitted,
  } = props;
  const {control, errors, setValue, watch} = formMethod;
  const prefix = `${supervisorsFieldName}.${idx}`;
  const [customError, setCustomError] = useState(null);
  const supervisorsFieldValue = useWatch({
    control,
    name: supervisorsFieldName,
    defaultValue: defaultValues?.supervisors || [],
  });
  const supervisorValue = supervisorsFieldValue[idx];

  const [isPresetUpperSupervisor, setIsPresetUpperSupervisor] = useState(
    !!supervisorValue?.upperSupervisor,
  );

  const jobNoError = get(errors, `${prefix}.jobNo`);
  const supervisorStatusError = get(errors, `${prefix}.supervisorStatus`);
  const upperSupervisorError = get(errors, `${prefix}.upperSupervisor`);

  const supervisorData = sortedEmployee.find(
    e => e.jobNo === supervisorValue?.jobNo,
  );
  const supervisorStatusValue = watch(`${prefix}.supervisorStatus`);
  const jobNoValue = watch(`${prefix}.jobNo`);
  const employeesValue = watch(`employees`);

  const showUpperSpervisorField = !(
    isEditRootNode && supervisorStatusValue === MAIN
  );
  let upperSupervisorOptions = [];
  if (supervisorStatusValue === MAIN) {
    upperSupervisorOptions = parentNodeSupervisors;
  }
  if (supervisorStatusValue === SECONDARY) {
    upperSupervisorOptions = mainSupervisors;
  }

  // 如果上層主管已經不是主要主管，清空欄位
  useEffect(() => {
    const isSecondary = supervisorValue?.supervisorStatus === SECONDARY;
    const notFoundMainSupervior =
      supervisorValue?.upperSupervisor &&
      !supervisorsFieldValue.find(
        f =>
          f.supervisorStatus === MAIN &&
          f.jobNo === supervisorValue?.upperSupervisor,
      );
    if (isSecondary && notFoundMainSupervior) {
      setValue(`${prefix}.upperSupervisor`, null);
    }
  }, [supervisorsFieldValue, supervisorValue, prefix, setValue]);

  // 預設職位序號00上層主管
  useEffect(() => {
    if (showUpperSpervisorField && !isPresetUpperSupervisor) {
      if (
        !supervisorValue?.upperSupervisor &&
        parentNodeSupervisors.length > 0
      ) {
        setValue(`${prefix}.upperSupervisor`, parentNodeSupervisors[0]?.jobNo);
        setIsPresetUpperSupervisor(true);
      }
    }
  }, [
    parentNodeSupervisors,
    supervisorValue,
    showUpperSpervisorField,
    isPresetUpperSupervisor,
  ]);

  useEffect(() => {
    if (
      defaultValues?.error === 'isUpperSupervisorNotExist' &&
      !supervisorValue?.upperSupervisor
    ) {
      setCustomError('上一層部門資料已變更，請重新確認上一層主管');
    }
  }, []);

  const addMainSupervisors = newSupervisor => {
    if (!newSupervisor?.jobNo) return;

    const isEmpty = !supervisorValue?.jobNo;
    if (isEmpty) {
      setMainSupervisors([...mainSupervisors, newSupervisor]);
    } else {
      const supervisorNotExist = !mainSupervisors.find(
        s => s.jobNo === newSupervisor.jobNo,
      );
      if (supervisorNotExist) {
        const isReplaceOld = mainSupervisors.find(
          s => s.jobNo === supervisorValue?.jobNo,
        );
        if (isReplaceOld) {
          setMainSupervisors(
            mainSupervisors
              .map(e => {
                if (e.jobNo === supervisorValue?.jobNo) {
                  return newSupervisor;
                }
                return e;
              })
              .sort(sortByEmployeeJobNo),
          );
        } else {
          setMainSupervisors(
            [...mainSupervisors, newSupervisor].sort(sortByEmployeeJobNo),
          );
        }
      }
    }
  };

  const removeMainSupervisors = () => {
    const newMainSupervisors = mainSupervisors
      .filter(e => e.jobNo !== supervisorValue?.jobNo)
      .sort(sortByEmployeeJobNo);
    setMainSupervisors(newMainSupervisors);
    return newMainSupervisors;
  };
  const removeEmployeeSupervisor = () => {
    let _unselectedEmployees = unselectedEmployees.slice();
    const currentEmployeeValue = employeesValue.filter(
      e => e.supervisor === supervisorValue?.jobNo,
    );
    const otherEmployeeValue = employeesValue.filter(
      e => e.supervisor !== supervisorValue?.jobNo,
    );

    if (currentEmployeeValue.length > 0) {
      _unselectedEmployees = _unselectedEmployees.map(e => {
        if (
          currentEmployeeValue.find(
            ce => ce.employeeNumber === e.employeeNumber,
          )
        ) {
          return {...e, supervisor: null};
        }
        return e;
      });
      setUnselectedEmployees(_unselectedEmployees);
      setValue('employees', otherEmployeeValue);
    }
  };

  return (
    <Fragment>
      {idx > 0 && <hr className={'col-[1/3] mt-[15px] mb-[15px]'} />}
      <Row>
        <FormItem label="部門主管" required errorMessage={jobNoError?.message}>
          <Controller
            render={({onChange, value}) => (
              <div data-test-id={`${prefix}.jobNo`}>
                <Select
                  value={sortedEmployee.find(e => e.jobNo === value)}
                  onChange={o => {
                    onChange(o.jobNo);
                    setValue(`${prefix}.effectiveDate`, o.onBoardingDate);
                    if (supervisorStatusValue === MAIN) {
                      addMainSupervisors(o);
                    }
                    if (jobNoValue) {
                      const updateEmployeesValue = employeesValue.map(e => {
                        if (e.supervisor === jobNoValue) {
                          return {...e, supervisor: o.jobNo};
                        }
                        return e;
                      });
                      setValue('employees', updateEmployeesValue);
                    }
                  }}
                  isError={isSubmitted && jobNoError}
                  isClearable={false}
                  placeholder="部門主管"
                  getOptionLabel={o => `${o.jobNo}/${o.name}`}
                  getOptionValue={o => o.jobNo}
                  filterOption={option => {
                    return (
                      option.value === value ||
                      !supervisorsFieldValue.some(f => f.jobNo === option.value)
                    );
                  }}
                  options={sortedEmployee}
                />
              </div>
            )}
            name={`${prefix}.jobNo`}
            control={control}
            rules={{required: '主管為必填'}}
            defaultValue={supervisor.jobNo}
          />
        </FormItem>
        <div className={'col-[2/3] flex'}>
          <FormItem label="職位序號" style={{width: '100px'}}>
            <Input
              readOnly
              value={`0${idx}`}
              data-test-id={`${prefix}.supervisorSeq`}
            />
          </FormItem>
          <FormItem label="主管到職日" style={{flex: '1 1 0%'}}>
            <Controller
              as={<Input readOnly />}
              name={`${prefix}.effectiveDate`}
              control={control}
            />
          </FormItem>
          {idx !== 0 && (
            <FormItem
              label={
                <Button
                  variant={'simple'}
                  colorSchema={'primary'}
                  onClick={() => {
                    if (supervisorStatusValue === MAIN) {
                      removeMainSupervisors();
                    }
                    removeEmployeeSupervisor();
                    onRemove();
                  }}
                  aria-label="delete-supervisor"
                  icon={<TrashIcon />}
                />
              }
            />
          )}
        </div>
        <FormItem
          label="委任狀態"
          required
          errorMessage={supervisorStatusError?.message}
        >
          <Controller
            render={({onChange, value}) => (
              <div data-test-id={`${prefix}.supervisorStatus`}>
                <Select
                  value={supervisorStatusOptions.find(o => o.value === value)}
                  onChange={o => {
                    onChange(o.value);
                    if (o.value === MAIN) {
                      addMainSupervisors(supervisorData);
                      if (
                        showUpperSpervisorField &&
                        parentNodeSupervisors.length > 0
                      ) {
                        // 須等上層主管欄位已經在畫面上ready在執行
                        setTimeout(() => {
                          setValue(
                            `${prefix}.upperSupervisor`,
                            parentNodeSupervisors[0]?.jobNo,
                          );
                        }, 100);
                      }
                    }
                    if (o.value === SECONDARY) {
                      const newSupervisors = removeMainSupervisors(); // 原本是第一個主要主管，避免預設到自己，先拿刪除結果
                      if (newSupervisors.length > 0) {
                        // 須等上層主管欄位已經在畫面上ready在執行
                        setTimeout(() => {
                          setValue(
                            `${prefix}.upperSupervisor`,
                            newSupervisors[0]?.jobNo,
                          );
                        }, 100);
                      }
                    }
                  }}
                  isError={isSubmitted && supervisorStatusError}
                  isClearable={false}
                  placeholder="請選擇委任狀態"
                  options={supervisorStatusOptions}
                  isDisabled={idx === 0}
                />
              </div>
            )}
            name={`${prefix}.supervisorStatus`}
            control={control}
            rules={{required: '委任狀態為必填'}}
            defaultValue={supervisor.supervisorStatus}
          />
        </FormItem>
        {showUpperSpervisorField && (
          <FormItem
            label={
              <div className="inline-flex items-center gap-[5px]">
                <span>
                  上一層主管<span className="text-red">*</span>
                </span>
                <Tooltip
                  content={
                    <div>
                      <div>
                        若委任狀態為正主管，由上一層部門的主管進行簽核；
                      </div>
                      <div>若委任狀態為副主管，由這一層部門的正主管簽核。</div>
                    </div>
                  }
                  isIcon
                >
                  <Button
                    variant="simple"
                    colorSchema="primary"
                    icon={<InfoIcon />}
                  />
                </Tooltip>
              </div>
            }
            errorMessage={
              isSubmitted && (customError || upperSupervisorError?.message)
            }
          >
            <Controller
              render={({onChange, value}) => (
                <div data-test-id={`${prefix}.upperSupervisor`}>
                  <Select
                    value={
                      upperSupervisorOptions.find(e => e.jobNo === value) ||
                      null
                    }
                    onChange={o => {
                      onChange(o?.jobNo);
                      if (customError) {
                        // 更改後清除自訂錯誤
                        setCustomError(null);
                      }
                    }}
                    isError={isSubmitted && upperSupervisorError}
                    placeholder="請選擇上一層主管"
                    getOptionLabel={o => `${o.jobNo}/${o.name}`}
                    getOptionValue={o => o.jobNo}
                    options={upperSupervisorOptions}
                  />
                </div>
              )}
              name={`${prefix}.upperSupervisor`}
              isClearable={false}
              control={control}
              rules={{required: '上一層主管為必填'}}
              defaultValue={supervisor.upperSupervisor}
            />
          </FormItem>
        )}
        {jobNoValue && (
          <div className={'col-[1/3]'}>
            <FormItem label="所屬員工">
              <div data-test-id={`${prefix}.employees`}>
                <Select
                  isMulti
                  value={employeesValue.filter(
                    e => e.supervisor === supervisorValue?.jobNo,
                  )}
                  onChange={(options, props) => {
                    const otherEmployeeValue = employeesValue.filter(
                      e => e.supervisor !== supervisorValue?.jobNo,
                    );
                    let _unselectedEmployees = unselectedEmployees.slice();
                    let _options = options.slice();
                    if (
                      props.action === 'remove-value' ||
                      props.action === 'clear'
                    ) {
                      const removedValues =
                        props.action === 'remove-value'
                          ? [props.removedValue]
                          : props.removedValues;
                      _unselectedEmployees = _unselectedEmployees.map(e => {
                        if (
                          removedValues.find(
                            v => v.employeeNumber === e.employeeNumber,
                          )
                        ) {
                          return {...e, supervisor: null};
                        }
                        return e;
                      });
                    }
                    if (props.action === 'select-option') {
                      _unselectedEmployees = _unselectedEmployees.map(e => {
                        if (e.employeeNumber === props.option.employeeNumber) {
                          return {...e, supervisor: supervisorValue?.jobNo};
                        }
                        return e;
                      });
                      _options = _options.map(o => {
                        return {...o, supervisor: supervisorValue?.jobNo};
                      });
                    }

                    setUnselectedEmployees(_unselectedEmployees);
                    setValue('employees', [...otherEmployeeValue, ..._options]);
                  }}
                  placeholder="請選擇所屬員工"
                  getOptionLabel={o => `${o.jobNo}/${o.lastName}${o.firstName}`}
                  getOptionValue={o => o.jobNo}
                  options={unselectedEmployees}
                  filterOption={option => {
                    return !option.data.supervisor;
                  }}
                />
              </div>
            </FormItem>
          </div>
        )}
      </Row>
    </Fragment>
  );
};
const Form = props => {
  const {
    onClose,
    onSubmit,
    defaultValues,
    employee,
    getUnselectedEmployee,
    checkDeptCodeRepeat,
    isEditRootNode,
    getParentNode,
    isConfirmed,
  } = props;
  // sync employee name
  const formatEmployees = defaultValues?.employees?.map(e => {
    const employeeData = employee.find(o => e.jobNo === o.jobNo) || e;
    employeeData.supervisor = e.supervisor;
    return employeeData;
  });
  const formMethod = useForm({
    defaultValues: defaultValues
      ? {
          ...defaultValues,
          employees: formatEmployees,
        }
      : undefined,
    mode: 'onChange',
  });
  const {control, formState, errors, handleSubmit, watch, trigger} = formMethod;
  const isSubmitted = formState.isSubmitted || isConfirmed;
  const [unselectedEmployees, setUnselectedEmployees] = useState(
    getUnselectedEmployee().concat(formatEmployees).sort(sortByEmployeeJobNo),
  );
  const [parentNodeSupervisors, setParentNodeSupervisors] = useState([]);
  const [mainSupervisors, setMainSupervisors] = useState([]);

  const sortedEmployee = employee.sort(sortByEmployeeJobNo);

  const supervisorsFieldName = 'supervisors';
  const supervisorsField = useFieldArray({
    control: control,
    name: supervisorsFieldName,
  });

  const addNewSupervisor = useCallback(() => {
    supervisorsField.append({
      jobNo: null,
      effectiveDate: null,
      positionOrder: null,
      supervisorStatus: MAIN,
      upperSupervisor: null,
    });
  }, [supervisorsField]);

  useEffect(() => {
    if (supervisorsField.fields.length === 0) {
      addNewSupervisor();
    } else {
      setMainSupervisors(
        employee.filter(e =>
          defaultValues?.supervisors?.some(
            s => s.jobNo === e.jobNo && s.supervisorStatus === 0,
          ),
        ),
      );
    }
  }, []);

  useEffect(() => {
    getParentNode(defaultValues.id).then(node => {
      setParentNodeSupervisors(
        node?.supervisors
          ?.filter(s => s.jobNo)
          .map(s => {
            return employee.find(e => e.jobNo === s.jobNo);
          }) || [],
      );
    });
  }, []);

  useEffect(() => {
    if (isConfirmed && defaultValues.error) {
      setTimeout(() => {
        // 須等hook-form初始化後呼叫，故使用setTimeout延遲呼叫時間
        trigger();
      }, 100);
    }
  }, []);

  return (
    <Dialog
      isOpen
      className={'!w-[864px] !max-w-[864px]'}
      aria-label="org-form"
    >
      <DialogTitle
        customStyle={{fontWeight: 400}}
        onClose={onClose}
        title="編輯部門資料"
      />
      <DialogContent className={'flex-col !px-0 !pt-0 !pb-[20px]'}>
        <SubHeader>部門基本資訊</SubHeader>
        <Controller as={<input hidden />} name={`id`} control={control} />
        <Controller
          as={<input hidden />}
          name={`employees`}
          control={control}
        />
        <div className={'grid grid-cols-2 gap-5'}>
          <FormItem
            label="部門名稱"
            required={true}
            errorMessage={errors.departmentName?.message}
          >
            <Controller
              render={formProps => (
                <Input
                  {...formProps}
                  isError={isSubmitted && errors.departmentName}
                  placeholder="請輸入部門名稱（繁體中文）"
                  data-test-id={'department-name'}
                />
              )}
              name="departmentName"
              control={control}
              rules={{required: '部門名稱為必填'}}
            />
          </FormItem>
          <FormItem
            label="部門代碼"
            required={true}
            errorMessage={[
              errors.departmentCodePrefix?.message,
              errors.departmentCodeSeqNo?.message,
            ]
              .filter(m => m)
              .join('、')}
          >
            <div className={'flex items-center justify-start'}>
              <Controller
                render={({onChange, value}) => (
                  <Input
                    data-test-id={'department-code-prefix'}
                    className={'!w-[calc(30%-10px)] !mr-[10px]'}
                    onChange={e => {
                      const value = e.target.value;
                      const isEmpty = !value;
                      const isEn = /^[a-zA-Z]+$/i.test(value);
                      if (isEmpty || isEn) {
                        onChange(value ? value.toUpperCase() : value);
                      }
                      if (isSubmitted) {
                        trigger('departmentCodeSeqNo');
                      }
                    }}
                    maxLength={2}
                    isError={
                      isSubmitted &&
                      (errors.departmentCodePrefix ||
                        errors.departmentCodeSeqNo?.type === 'isRepeat')
                    }
                    value={value}
                    placeholder="英文"
                  />
                )}
                name="departmentCodePrefix"
                control={control}
                rules={{required: '前置碼為必填'}}
              />
              <Controller
                render={({onChange, value}) => (
                  <Input
                    data-test-id={'department-code-seq-no'}
                    className={'!w-[70%]'}
                    onChange={e => {
                      const value = e.target.value;
                      const isEmpty = !value;
                      const isNumber = /^\d+$/i.test(e.target.value);
                      if (isEmpty || isNumber) {
                        onChange(value ? value.toUpperCase() : value);
                      }
                    }}
                    maxLength={3}
                    isError={isSubmitted && errors.departmentCodeSeqNo}
                    value={value}
                    placeholder="數字"
                  />
                )}
                name="departmentCodeSeqNo"
                control={control}
                rules={{
                  required: '流水號為必填',
                  validate: {
                    isRepeat: v => {
                      const prefix = watch('departmentCodePrefix');
                      const ignoreSelf =
                        defaultValues.departmentCodeSeqNo === v;
                      return (
                        ignoreSelf ||
                        !checkDeptCodeRepeat(prefix, v) ||
                        '部門代碼已重複，請調整前置碼或流水號。'
                      );
                    },
                  },
                }}
              />
            </div>
          </FormItem>
          <FormItem label="國家代碼" required={true}>
            <Controller
              render={({onChange, value}) => (
                <div data-test-id={'country-code'}>
                  <Select
                    value={value ? {value, label: value} : null}
                    onChange={o => onChange(o.value)}
                    isError={isSubmitted && errors.countryCode}
                    placeholder="請選擇國家代碼"
                    options={NATION_CODE_OPTIONS.map(v => ({
                      label: v,
                      value: v,
                    }))}
                  />
                </div>
              )}
              name="countryCode"
              control={control}
              rules={{required: '國家代碼為必填'}}
            />
          </FormItem>
          <FormItem
            label="所在縣市"
            required={true}
            errorMessage={errors.countyName?.message}
          >
            <Controller
              render={({onChange, value}) => (
                <div data-test-id={'country-name'}>
                  <Select
                    value={value ? {value, label: value} : null}
                    onChange={o => onChange(o.value)}
                    isError={isSubmitted && errors.countyName}
                    placeholder="請選擇所在縣市"
                    options={TW_COUNTY_OPTIONS.map(v => ({label: v, value: v}))}
                  />
                </div>
              )}
              name="countyName"
              control={control}
              rules={{required: '所在縣市為必填'}}
            />
          </FormItem>
        </div>
        <SubHeader>部門主管與員工</SubHeader>
        {supervisorsField.fields.map((supervisor, idx) => {
          return (
            <SupervisorField
              key={supervisor.id}
              supervisor={supervisor}
              idx={idx}
              formMethod={formMethod}
              supervisorsFieldName={supervisorsFieldName}
              sortedEmployee={sortedEmployee}
              mainSupervisors={mainSupervisors}
              unselectedEmployees={unselectedEmployees}
              setMainSupervisors={setMainSupervisors}
              setUnselectedEmployees={setUnselectedEmployees}
              isEditRootNode={isEditRootNode}
              defaultValues={defaultValues}
              onRemove={() => supervisorsField.remove(idx)}
              parentNodeSupervisors={parentNodeSupervisors}
              isSubmitted={isSubmitted}
            />
          );
        })}
        {supervisorsField.fields.length < 6 && (
          <div className={'col-[1/3] mt-[20px] mb-[20px]'}>
            <Button
              variant={'simple'}
              colorSchema={'primary'}
              onClick={() => {
                supervisorsField.append({
                  jobNo: null,
                  effectiveDate: null,
                  positionOrder: null,
                  supervisorStatus: null,
                  upperSupervisor: null,
                });
              }}
              aria-label="add-supervisor"
              icon={<CirclePlusIcon />}
            >
              新增主管
            </Button>
          </div>
        )}
        <DevTool control={control} />
      </DialogContent>
      <DialogFooter>
        <ButtonGroup>
          <Button
            variant={'outline'}
            colorSchema={'primary'}
            onClick={onClose}
            aria-label="close-btn"
          >
            取消
          </Button>
          <Button
            variant={'solid'}
            colorSchema={'primary'}
            onClick={handleSubmit(onSubmit)}
            aria-label="confirm-btn"
          >
            儲存
          </Button>
        </ButtonGroup>
      </DialogFooter>
    </Dialog>
  );
};

export default Form;
